// @flow
import * as React from 'react';
import type { ChildrenArray } from 'react';
import {
  usePropsValidation,
  types as t,
} from '../../validation/runtime-checks/index.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/types.js';
import type { MathString } from '../../types.js';
import { mathStringChecker } from '../../types.js';
import { productColor } from '../../designSystem.js';
import { InternalHeading } from '../../InternalHeading.js';
import {
  InternalSection,
  InternalDescription,
} from '../common-components/internal-components.js';
import { INVTNInstructionalSupports } from './INVTNInstructionalSupports.js';
import { INVDownloadableAssets } from '../common-components/INVDownloadableAssets.js';

export function INVTNOverview(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVTNOverviewDescription>
    | React$Element<typeof INVTNSkillSection>
    | React$Element<typeof INVTNPreparationSection>,
  >,
  timeEstimate?: number,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNOverview);
  if (failedValidation) return null;

  const { children, timeEstimate } = props;
  return (
    <div style={{ color: productColor.shuttleGrey, marginBottom: 40 }}>
      <InternalHeading level={2}>Overview</InternalHeading>
      <div style={{ marginTop: 12 }}>
        <span style={{ fontWeight: 600 }}>Timing:</span> {timeEstimate} mins
      </div>
      {children}
    </div>
  );
}
INVTNOverview.spec = {
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(INVTNOverviewDescription),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNSkillSection),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(INVTNPreparationSection),
    },
  ),
  timeEstimate: t.optional(t.number),
};

export function INVTNOverviewDescription(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNOverviewDescription,
  );
  if (failedValidation) return null;

  return <InternalDescription>{props.children}</InternalDescription>;
}
INVTNOverviewDescription.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function INVTNSkillSection(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVTNPrerequisiteSkills>
    | React$Element<typeof INVTNMainSkills>
    | React$Element<typeof INVTNExtensionSkills>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNSkillSection);
  if (failedValidation) return null;

  return (
    <InternalSection title="Understanding and skills">
      {props.children}
    </InternalSection>
  );
}
INVTNSkillSection.spec = {
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(INVTNPrerequisiteSkills) },
    { min: 1, max: 1, elementType: t.elementType(INVTNMainSkills) },
    { min: 0, max: 1, elementType: t.elementType(INVTNExtensionSkills) },
  ),
};

export function INVTNPreparationSection(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVTNPreparationList>
    | React$Element<typeof INVTNInstructionalSupports>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNPreparationSection,
  );
  if (failedValidation) return null;

  return (
    <InternalSection title="Preparation and materials">
      {props.children}
    </InternalSection>
  );
}
INVTNPreparationSection.spec = {
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(INVTNPreparationList) },
    { min: 0, max: 1, elementType: t.elementType(INVTNInstructionalSupports) },
  ),
};

export function INVTNPrerequisiteSkills(props: {|
  children: ChildrenArray<React$Element<typeof INVTNSkill>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNPrerequisiteSkills,
  );
  if (failedValidation) return null;

  return (
    <InternalSkillGroup heading="Will use">{props.children}</InternalSkillGroup>
  );
}
INVTNPrerequisiteSkills.spec = {
  children: t.childrenArray(t.elementType(INVTNSkill)),
};

export function INVTNMainSkills(props: {|
  children: ChildrenArray<React$Element<typeof INVTNSkill>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNMainSkills);
  if (failedValidation) return null;

  return (
    <InternalSkillGroup heading="Will develop">
      {props.children}
    </InternalSkillGroup>
  );
}
INVTNMainSkills.spec = {
  children: t.childrenArray(t.elementType(INVTNSkill)),
};

export function INVTNExtensionSkills(props: {|
  children: ChildrenArray<React$Element<typeof INVTNSkill>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNExtensionSkills);
  if (failedValidation) return null;

  return (
    <InternalSkillGroup heading="Could extend">
      {props.children}
    </InternalSkillGroup>
  );
}
INVTNExtensionSkills.spec = {
  children: t.childrenArray(t.elementType(INVTNSkill)),
};

export function INVTNPreparationList(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVTNPreparationListItem>
    | React$Element<typeof INVDownloadableAssets>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNPreparationList);

  if (failedValidation) return null;

  return (
    <ul>
      {React.Children.map(props.children, (child, idx) => (
        <li key={idx}>{child}</li>
      ))}
    </ul>
  );
}
INVTNPreparationList.spec = {
  children: childrenArrayMatcher(
    {
      min: 0,
      max: Infinity,
      elementType: t.elementType(INVTNPreparationListItem),
    },
    { min: 0, max: 1, elementType: t.elementType(INVDownloadableAssets) },
  ),
};

export function INVTNSkill(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, INVTNSkill);
  if (failedValidation) return null;

  return (
    <div
      style={{
        background: productColor.aliceBlue,
        borderRadius: 8,
        margin: '6px 0',
        padding: 16,
      }}
    >
      {props.children}
    </div>
  );
}
INVTNSkill.spec = {
  children: t.childrenArray(mathStringChecker),
};

function InternalSkillGroup(props: {|
  heading: string,
  children: ChildrenArray<React$Element<typeof INVTNSkill>>,
|}) {
  const { heading, children } = props;
  return (
    <div style={{ marginTop: 12 }}>
      <h3
        style={{
          fontWeight: 600,
          fontSize: 16,
          color: productColor.cloudburst,
          lineHeight: 1.375,
          marginTop: 6,
          marginBottom: 6,
        }}
      >
        {heading}
      </h3>
      {children}
    </div>
  );
}

export function INVTNPreparationListItem(props: {|
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNPreparationListItem,
  );
  if (failedValidation) return null;

  return props.children;
}
INVTNPreparationListItem.spec = {
  children: t.childrenArray(mathStringChecker),
};
